import { Square } from 'lucide-react';
import type { ConversationTurn } from '../api/conversation';
import { MessageAttachments } from './MessageAttachments';
import { MessageContent } from './MessageContent';
import { MessageResources } from './MessageResources';
import { MessageSources } from './MessageSources';
import { FallbackDecisionButtons } from './FallbackDecisionButtons';
import { useMe } from '../auth/MeContext';
import { useAudioReply } from './AudioReplyContext';
import { useT } from '../I18nContext';

/**
 * One bubble. Right-aligned for the user, left-aligned for the
 * assistant. Avatar on the assistant side (Locksmith mark);
 * initials chip on the user side. Order inside the bubble:
 *   1. Sources (assistant only) - small text above the body
 *   2. Attachments (user only) - image thumbnails above the body
 *   3. Content - markdown for assistant, plain text for user
 *   4. Yes/No buttons when awaiting_fallback_decision is set
 *   5. Stop-audio control (assistant only) when this turn's
 *      audio is currently being read aloud
 */
interface MessageProps {
  turn: ConversationTurn;
}

export function Message({ turn }: MessageProps) {
  const { me } = useMe();
  const { playingFor, stop } = useAudioReply();
  const t = useT();
  const isUser = turn.role === 'user';
  const sources = turn.sources ?? [];
  const resources = turn.resources ?? [];
  const attachments = turn.attachment_ids ?? [];
  // Show the stop control on this bubble only while ITS audio is
  // playing. The stop button in the composer toggle is the global
  // counterpart; both call the same context.stop().
  const isReadingThisTurn = !isUser && !!turn.timestamp && playingFor === turn.timestamp;

  return (
    <div className={'message-row' + (isUser ? ' is-user' : ' is-assistant')}>
      {!isUser && (
        <div className="message-avatar message-avatar-assistant" aria-hidden>
          {/* Lowercase 'L' on an accent square - the Locksmith
              shorthand. Sticking with a glyph rather than the full
              logo PNG so the avatar reads as a chat persona, not
              a brand stamp on every message. */}
          L
        </div>
      )}

      <div className="message-bubble">
        {/* Order matters: sources/attachments above the body
            visually so the reader knows the context before they
            read the answer. */}
        {!isUser && sources.length > 0 && <MessageSources sources={sources} />}
        {isUser && attachments.length > 0 && (
          <MessageAttachments attachmentIds={attachments} />
        )}
        <MessageContent content={turn.content} isUser={isUser} />
        {!isUser && turn.awaiting_fallback_decision && (
          <FallbackDecisionButtons />
        )}
        {isReadingThisTurn && (
          <button
            type="button"
            className="message-stop-audio-btn"
            onClick={stop}
            title={t('message.stop_reading_title')}
            aria-label={t('message.stop_reading_aria')}
          >
            <Square size={11} />
            <span>{t('message.stop_reading')}</span>
          </button>
        )}
        {!isUser && resources.length > 0 && (
          <MessageResources resources={resources} />
        )}
      </div>

      {isUser && (
        <div className="message-avatar message-avatar-user" aria-hidden>
          {me?.user.initials ?? 'U'}
        </div>
      )}
    </div>
  );
}
